import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getWorkspaceDetail, updateWorkspaceActive, deleteWorkspace } from "@/lib/admin";
import { useAdminQuery } from "@/hooks/useAdminQuery";

export default function AdminCompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, setData, loading, error, setError } = useAdminQuery(() => getWorkspaceDetail(id), [id]);

  async function handleToggleActive() {
    if (!data) return;
    setBusy(true);
    try {
      const { workspace: updated } = await updateWorkspaceActive(data.workspace.id, !data.workspace.active);
      setData((prev) => (prev ? { ...prev, workspace: updated } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update company");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!data) return;
    setBusy(true);
    try {
      await deleteWorkspace(data.workspace.id);
      setLocation("/admin/companies");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete company");
      setBusy(false);
    }
  }

  if (error && !data) {
    return <p className="text-sm text-destructive">{error}</p>;
  }
  if (loading || !data) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  const { workspace, assetsByCountry } = data;
  const totalAssets = assetsByCountry.reduce((sum, row) => sum + row.count, 0);
  const nameMatches = confirmText === workspace.name;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-heading font-extrabold">{workspace.name}</h1>
        <span className={workspace.active ? "text-emerald-600" : "text-muted-foreground"}>
          {workspace.active ? "Active" : "Disabled"}
        </span>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Company info</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Sector</dt>
              <dd className="capitalize">{workspace.sector}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd>{workspace.createdAt ? new Date(workspace.createdAt).toLocaleDateString() : "—"}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assets</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">{totalAssets}</p>
          <ul className="mt-4 space-y-1 text-sm">
            {assetsByCountry.map((row) => (
              <li key={row.country} className="flex justify-between">
                <span>{row.country}</span>
                <span className="font-medium">{row.count}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assessment</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Assessment status and results aren't available here yet — this section
            will be filled in once the assessment/results model is decided.
          </p>
        </CardContent>
      </Card>

      <section className="flex items-center gap-3">
        <Button variant={workspace.active ? "destructive" : "default"} disabled={busy} onClick={handleToggleActive}>
          {workspace.active ? "Disable company" : "Enable company"}
        </Button>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setConfirmText(""); }}>
          <DialogTrigger asChild>
            <Button variant="outline">Delete company</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {workspace.name}?</DialogTitle>
              <DialogDescription>
                This permanently deletes the company and everything that belongs to
                it — its users, assets, and value-chain data. This cannot be undone.
                Type <strong>{workspace.name}</strong> to confirm.
              </DialogDescription>
            </DialogHeader>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={workspace.name} />
            <DialogFooter>
              <Button variant="destructive" disabled={!nameMatches || busy} onClick={handleDelete}>
                Delete permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </div>
  );
}
